<template>
  <modal-component
    :is-displayed="isEditModalOpened"
    :no-padding="true"
    :title="$t('Displays.EditTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <map-location-selector
      v-if="isMapLocationModalOpened"
      :coordinates-prop="{ lat: display.latitude, lng: display.longitude }"
      :is-map-location-modal-opened="isMapLocationModalOpened"
      @toggle-modal="toggleMapLocationModal"
      @get-coordinates="getMarkerCoordinates"
    />
    <form @submit.prevent="handleSubmit">
      <form-wrapper :is-form-in-modal="true">
        <form-divider>
          <input-field
            v-model="display.name"
            :error-message="v$?.display['name']?.$errors[0]?.$message"
            :is-error="v$?.display['name']?.$error"
            :label="$t('Displays.Name')"
            :required="true"
          />
          <div
            class="grid grid-cols-2 gap-6"
            style="grid-template-columns: auto min-content"
          >
            <div>
              <input-field
                v-model="display.latitude"
                :error-message="v$?.display['latitude']?.$errors[0]?.$message"
                :is-error="v$?.display['latitude']?.$error"
                :label="$t('Displays.Latitude')"
                :required="true"
              />
              <input-field
                v-model="display.longitude"
                :error-message="v$?.display['longitude']?.$errors[0]?.$message"
                :is-error="v$?.display['longitude']?.$error"
                :label="$t('Displays.Longitude')"
                :required="true"
              />
            </div>
            <div class="pb-3 pt-10">
              <the-button
                :icon="['fas', 'earth-europe']"
                button-type="primaryButton"
                type="button"
                @execute-method="toggleMapLocationModal"
              />
            </div>
          </div>
          <input-field
            v-model="display.imei"
            :error-message="v$?.display['imei']?.$errors[0]?.$message"
            :is-error="v$?.display['imei']?.$error"
            :label="$t('Displays.IMEI')"
            :required="true"
          />
          <checkbox-input
            v-model="display.active"
            :is-checked="display.active"
            :label="$t('Displays.Active')"
          />
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form-wrapper>
    </form>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import InputField from "@/core/Components/FormElements/InputField.vue";
import CheckboxInput from "@/core/Components/FormElements/CheckboxInput.vue";
import {
  Display,
  EditDisplay,
} from "@/modules/Parkom/Submodules/Displays/Services/DisplayTypes";
import TheButton from "@/core/Components/TheButton.vue";
import MapLocationSelector from "@/core/Components/MapLocationSelector.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import { useToast } from "vue-toastification";
import {
  ErrorMessageFormatter,
  isString,
} from "@/core/Services/GlobalFunctions";
import DisplayService from "@/modules/Parkom/Submodules/Displays/Services/DisplayService";
import useValidate from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import VSelect from "vue-select";

export default defineComponent({
  components: {
    VSelect,
    SmallLoader,
    ButtonWrapper,
    CheckboxInput,
    InputField,
    FormDivider,
    FormWrapper,
    ModalComponent,
    TheButton,
    MapLocationSelector,
  },
  props: {
    isEditModalOpened: Boolean,
    refreshData: {
      type: Function,
      required: true,
    },
    displayProp: {
      type: Object as PropType<Display>,
      required: true,
    },
  },
  emits: ["toggle-modal"],
  data() {
    return {
      display: {} as EditDisplay,
      displayService: new DisplayService(),
      isMapLocationModalOpened: false,
      isSubmitLoading: false,
      toast: useToast(),
      v$: useValidate(),
    };
  },
  validations() {
    return {
      display: {
        name: {
          required,
        },
        latitude: {
          required,
        },
        longitude: {
          required,
        },
        imei: {
          required,
          minLength: minLength(15),
          maxLength: maxLength(15),
        },
      },
    };
  },
  async created() {
    const { nodes, ...rest } = this.displayProp;
    this.display = rest;
  },
  methods: {
    async handleSubmit() {
      await this.v$.$validate();
      if (!this.v$.$error) {
        this.isSubmitLoading = true;
        try {
          await this.displayService.updateDisplay(this.display);
          this.toast.success("Display successfully updated!");
          this.$emit("toggle-modal");
          await this.refreshData();
        } catch (error: any) {
          if (!isString(error.response.data)) {
            this.toast.error(ErrorMessageFormatter(error));
          }
          this.toast.error(error.response.data);
        } finally {
          this.isSubmitLoading = false;
        }
      }
    },
    toggleMapLocationModal() {
      this.isMapLocationModalOpened = !this.isMapLocationModalOpened;
    },
    getMarkerCoordinates(coordinates: { lat: number; lng: number }) {
      this.display.latitude = coordinates.lat;
      this.display.longitude = coordinates.lng;
    },
  },
});
</script>

<style scoped></style>
